import {
  FieldSpec,
  InstanceProperties,
  RichType,
  getEnumValuesFromMap,
} from 'lib/rich'
import { UnitShorthand } from 'types/unit-shorthand'
import { groups, type_names } from '@toolpath/tools'
import { unit_spec } from './common'

type ToolType = keyof typeof type_names
type GroupType = keyof typeof groups

function expandForTypes(for_types) {
  return for_types.flatMap((ft) => groups[ft] || ft)
}

function typeIn(...types: (ToolType | GroupType)[]): {
  type: { in: ToolType[] }
} {
  return { type: { in: expandForTypes(types) } }
}

// NOTE: Make sure this list is in sync with TOOLTYPE_MAP in fusion-tools-to-toolpath-tools.ts
export const supportedTypes = [
  'flat end mill',
  'bull nose end mill',
  'ball end mill',
  'face mill',
  'chamfer mill',
  'slot mill',
  'dovetail mill',
  'radius mill',
  'thread mill',

  'drill',
  'spot drill',
  'center drill',
  'reamer',

  'boring bar',
  'counter bore',
  'counter sink',

  'tap right hand',
  'tap left hand',
]

export const supportedTypeEnumValues = supportedTypes.map((value) => ({
  value,
  title: type_names[value],
}))

const allTypeEnumValues: { value: ToolType; title: string }[] = (
  Object.entries(type_names) as [ToolType, string][]
).map(([value, title]) => ({ value, title }))

const typeEnumValues = supportedTypeEnumValues

const supportTurning = false

export namespace ShaftSegment {
  export const Version = 1

  export type Data = {
    height: number
    upperDiameter: number
    lowerDiameter: number
  }

  export const Spec: readonly (FieldSpec & { id: keyof Data })[] = [
    {
      id: 'height',
      title: 'Height',
      kind: 'length',
      defaultValueMM: 2,
      defaultValueIN: 0.1,
      gte: 0,
      required: true,
    },
    {
      id: 'upperDiameter',
      title: 'Upper Diameter',
      kind: 'length',
      defaultValueMM: 6,
      defaultValueIN: 0.25,
      gt: 0,
      required: true,
    },
    {
      id: 'lowerDiameter',
      title: 'Lower Diameter',
      kind: 'length',
      defaultValueMM: 6,
      defaultValueIN: 0.25,
      gt: 0,
      required: true,
    },
  ] as const
}

export class Tool_V1 extends RichType {
  //////////// general ////////////

  guid?: string
  static guid_spec: FieldSpec = {
    title: 'GUID',
    kind: 'guid',
    hidden: true,
  }

  /*
  sourceSystem: string = 'fusion'
  static sourceSystem_spec: FieldSpec = {
    title: 'Source System',
    kind: 'enum',
    forceNarrow: true,
    defaultValue: 'fusion',
    hidden: true,
  }
*/

  description: string = ''
  static description_spec: FieldSpec = {
    title: 'Description',
    kind: 'text',
    defaultValue: '',
    noLabel: true,

    componentId: 'ToolDescription',
  }

  static vendorGroup_spec = { title: 'Vendor', kind: 'group' }

  vendor: string = ''
  static vendor_spec: FieldSpec = {
    title: 'Vendor',
    kind: 'text',
    defaultValue: '',
    //noLabel: true,
  }

  productId: string = ''
  static productId_spec: FieldSpec = {
    title: 'Product ID',
    kind: 'text',
    defaultValue: '',
  }

  productLink: string = ''
  static productLink_spec: FieldSpec = {
    title: 'Product Link',
    kind: 'text',
    defaultValue: '',
  }

  //////////// cutter ////////////

  static groupCutter_spec: FieldSpec = { title: 'Type', kind: 'group' }

  static type_spec: FieldSpec = {
    title: 'Type',
    kind: 'enum',
    enumValues: typeEnumValues,
    defaultValue: typeEnumValues[0].value,
    required: true,

    noLabel: true,
    forceNarrow: true,
  }
  type: ToolType

  static unit_spec: FieldSpec = {
    ...unit_spec,

    forceNarrow: true,
    hidden: false,
  }
  unit: UnitShorthand = 'mm'

  /*
  static version: number = 1
}

export class Tool_V1 extends ToolBase_V1 {
*/

  geometry_clockwise?: boolean // ToolMilling, ToolDrill, boring bar, counter bore, reamer, counter sink
  static geometry_clockwise_spec: FieldSpec = {
    title: 'Clockwise Spindle Rotation',
    kind: 'boolean',
    defaultValue: true,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolDrill',
      'boring bar',
      'counter bore',
      'reamer',
      'counter sink',
    ),
    required: true,
  }

  geometry_numberOfFlutes?: number // ToolMilling, ToolHoleMaking
  static geometry_numberOfFlutes_spec: FieldSpec = {
    title: 'Number of Flutes',
    kind: 'integer',
    defaultValue: 3,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking'),
    required: true,
    gt: 0, // TODO: Should allow 0 for taps (forming taps)
  }

  material?: string // ToolMilling, ToolHoleMaking, ToolTurning
  static material_spec: FieldSpec = {
    title: 'Material',
    kind: 'enum',
    defaultValue: 'carbide',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'ToolTurning'),
    enumValues: getEnumValuesFromMap({
      unspecified: 'Unspecified',
      carbide: 'Carbide',
      'ti coated': 'Ti Coated',
      ceramics: 'Ceramic',
      hss: 'HSS',
    }),
    required: true,

    forceNarrow: true,
  }

  geometry_taperedType?: string // tapered mill
  static geometry_taperedType_spec: FieldSpec = {
    title: 'Tapered Type',
    kind: 'enum',
    defaultValue: 'tapered_bull_nose',
    enabledWhen: typeIn('tapered mill'),
    enumValues: getEnumValuesFromMap({
      tapered_bull_nose: 'Bull nose',
      tapered_ball_nose: 'Ball',
    }),
    required: true,

    forceNarrow: true,
  }

  geometry_coolantSupport?: boolean // ToolMilling, ToolHoleMaking
  static geometry_coolantSupport_spec: FieldSpec = {
    title: 'Coolant Support',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking'),

    hidden: true, // not currently shown anywhere in Fusion either, but in data
  }

  //////////// cutter_geometry ////////////

  static groupGeometry_spec: FieldSpec = {
    title: 'Geometry',
    kind: 'group',
  }

  geometry_cuttingDiameter?: number // ToolMilling, ToolHoleMaking, probe
  geometry_cornerRadius?: number // bull nose end mill, face mill, tapered mill, radius mill, dovetail mill, slot mill, circle segment lens, turning general, turning boring, turning grooving
  geometry_shaftDiameter?: number // ToolEndMill, ToolHoleMaking, circle segment barrel, probe
  geometry_overallLength?: number // ToolMilling, ToolHoleMaking, turning grooving, turning threading
  geometry_shoulderLength?: number // ToolEndMill, ToolHoleMaking
  geometry_shoulderDiameter?: number // ToolEndMill, ToolCircleSegmentMill
  geometry_fluteLength?: number // ToolMilling, ToolHoleMaking
  geometry_taperAngle?: number // face mill, tapered mill, chamfer mill, dovetail mill, circle segment taper, center drill
  geometry_numberOfTeeth?: number // thread mill
  geometry_threadPitch?: number // ToolTap, thread mill, turning threading
  geometry_threadProfileAngle?: number // thread mill, turning threading
  geometry_tipAngle?: number // ToolDrill, counter sink
  geometry_tipDiameter?: number // chamfer mill, circle segment taper, center drill, spot drill, counter sink
  geometry_tipLength?: number // radius mill, center drill
  geometry_tipOffset?: number // form mill
  geometry_axialDistance?: number // circle segment barrel
  geometry_profileRadius?: number // circle segment barrel, circle segment oval, circle segment taper
  geometry_upperRadius?: number // circle segment barrel, circle segment taper
  geometry_lowerRadius?: number // ToolCircleSegmentMill

  static geometry_cuttingDiameter_spec: FieldSpec = {
    title: 'Diameter',
    kind: 'length',
    defaultValueMM: 6,
    defaultValueIN: 0.25,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'probe'),
    required: true,
    gt: 0,
  }

  static geometry_cornerRadius_spec: FieldSpec = {
    title: 'Corner Radius',
    kind: 'length',
    defaultValueMM: 0.75,
    defaultValueIN: 0.03125,
    enabledWhen: typeIn(
      'bull nose end mill',
      'face mill',
      'tapered mill',
      'radius mill',
      'dovetail mill',
      'slot mill',
      'circle segment lens',
      'turning general',
      'turning boring',
      'turning grooving',
    ),
    required: true,
    gte: 0,
  }

  static geometry_shaftDiameter_spec: FieldSpec = {
    title: 'Shaft Diameter',
    kind: 'length',
    defaultValueMM: 6,
    defaultValueIN: 0.25,
    enabledWhen: typeIn(
      'ToolEndMill',
      'ToolHoleMaking',
      'circle segment barrel',
      'probe',
    ),
    required: true,
    gt: 0,
  }

  static geometry_overallLength_spec: FieldSpec = {
    title: 'Overall Length',
    kind: 'length',
    defaultValueMM: 36,
    defaultValueIN: 1.5,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'turning grooving',
      'turning threading',
    ),
    required: true,
    gte: 'geometry_protrudingLength',
  }

  static geometry_shoulderLength_spec: FieldSpec = {
    title: 'Shoulder Length',
    kind: 'length',
    defaultValueMM: 21,
    defaultValueIN: 0.875,
    enabledWhen: typeIn('ToolEndMill', 'ToolHoleMaking'),
    required: true,
    gte: 'geometry_fluteLength',
  }

  static geometry_shoulderDiameter_spec: FieldSpec = {
    title: 'Shoulder Diameter',
    kind: 'length',
    enabledWhen: typeIn('ToolEndMill', 'ToolCircleSegmentMill'),
    gt: 0.0,
    dependencies: keys('geometry_shaftDiameter'),
    compute: (values: Tool_V1_Data) => values?.geometry_shaftDiameter,
    computeAlways: true,

    hidden: true, // not currently shown anywhere in Fusion either, but in data
  }

  static geometry_fluteLength_spec: FieldSpec = {
    title: 'Flute Length',
    kind: 'length',
    defaultValueMM: 18,
    defaultValueIN: 0.75,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking'),
    required: true,
    gte: 0.0, // fusion allows zero, but has a warning
  }

  static geometry_taperAngle_spec: FieldSpec = {
    title: 'Taper Angle',
    kind: 'angle',
    defaultValue: 10,
    enabledWhen: typeIn(
      'face mill',
      'tapered mill',
      'chamfer mill',
      'dovetail mill',
      'circle segment taper',
      'center drill',
    ),
    required: true,
    gte: 0.0,
  }

  static geometry_numberOfTeeth_spec: FieldSpec = {
    title: 'Number of Teeth',
    kind: 'integer',
    defaultValue: 1,
    enabledWhen: typeIn('thread mill'),
    required: true,
    gt: 0.0,
  }

  static geometry_threadPitch_spec: FieldSpec = {
    title: 'Thread Pitch',
    kind: 'length',
    defaultValueMM: 1,
    defaultValueIN: 0.04,
    enabledWhen: typeIn('ToolTap', 'thread mill', 'turning threading'),
    required: true,
    gt: 0.0,
  }

  static geometry_threadProfileAngle_spec: FieldSpec = {
    title: 'Thread Profile Angle',
    kind: 'angle',
    defaultValue: 60,
    enabledWhen: typeIn('thread mill', 'turning threading'),
    required: true,
    gt: 0.0,
  }

  static geometry_tipAngle_spec: FieldSpec = {
    title: 'Tip Angle',
    kind: 'angle',
    defaultValue: 120,
    enabledWhen: typeIn('ToolDrill', 'counter sink'),
    required: true,
    gt: 0.0,
  }

  static geometry_tipDiameter_spec: FieldSpec = {
    title: 'Tip Diameter',
    kind: 'length',
    defaultValueMM: 0,
    defaultValueIN: 0, // 0.08333 for center drill
    enabledWhen: typeIn(
      'chamfer mill',
      'circle segment taper',
      'center drill',
      'spot drill',
      'counter sink',
    ),
    required: true,
    gte: 0.0,
  }

  static geometry_tipLength_spec: FieldSpec = {
    title: 'Tip Length',
    kind: 'length',
    defaultValueMM: 0,
    defaultValueIN: 0, // 0.08333 for center drill
    enabledWhen: typeIn('radius mill', 'center drill'),
    required: true,
    gte: 0.0,
  }

  static geometry_tipOffset_spec: FieldSpec = {
    title: 'Tip Offset',
    kind: 'length',
    defaultValueMM: 0,
    enabledWhen: typeIn('form mill'),
    required: true,
    gte: 0.0,
  }

  static geometry_axialDistance_spec: FieldSpec = {
    title: 'Axial Distance',
    kind: 'length',
    defaultValueMM: 4,
    enabledWhen: typeIn('circle segment barrel'),
    required: true,
    gte: 0.0,
  }

  static geometry_profileRadius_spec: FieldSpec = {
    title: 'Profile Radius',
    kind: 'length',
    defaultValueMM: 8,
    enabledWhen: typeIn(
      'circle segment barrel',
      'circle segment oval',
      'circle segment taper',
    ),
    required: true,
    gt: 0.0,
  }

  static geometry_upperRadius_spec: FieldSpec = {
    title: 'Upper Radius',
    kind: 'length',
    defaultValueMM: 1,
    enabledWhen: typeIn('circle segment barrel', 'circle segment taper'),
    required: true,
    gt: 0.0,
  }

  static geometry_lowerRadius_spec: FieldSpec = {
    title: 'Lower Radius',
    kind: 'length',
    defaultValueMM: 1,
    enabledWhen: typeIn('ToolCircleSegmentMill'),
    required: true,
    gt: 0.0,
  }

  //////////// cutter_assembly ////////////

  static groupAssembly_spec: FieldSpec = {
    title: 'Assembly',
    kind: 'group',
  }

  geometry_protrudingLength?: number // ToolMilling, ToolHoleMaking, probe
  geometry_assemblyGaugeLength?: number // ToolMilling, ToolHoleMaking, probe

  static geometry_protrudingLength_spec: FieldSpec = {
    title: 'Length Below Holder',
    kind: 'length',
    defaultValueMM: 24,
    defaultValueIN: 1,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'probe'),
    required: true,
    gte: 'geometry_shoulderLength',
  }

  static geometry_assemblyGaugeLength_spec: FieldSpec = {
    title: 'Tool Assembly Gauge Length',
    inlineTitle: 'Gauge Length',
    kind: 'length',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'probe'),
    required: true,
    compute: (values) => {
      const holderGaugeLength = 0.0
      return values?.geometry_protrudingLength + holderGaugeLength
    },
  }

  //////////// shaft ////////////

  /*
  static groupShaft_spec: FieldSpec = { title: 'Shaft', kind: 'group' }

  shaft_segments?: {
    height: number
    upperDiameter: number
    lowerDiameter: number
  }[] // ToolMilling, ToolHoleMaking, probe

  static shaft_segments_spec: FieldSpec = {
    title: 'Shaft Segments',
    inlineTitle: 'Segments',
    kind: 'array',
    itemSpecs: ShaftSegment.Spec,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'probe'),
  }
  */

  //////////// post ////////////

  static groupPost_spec: FieldSpec = { title: 'Post', kind: 'group' }

  post_number?: number // Tool
  static post_number_spec: FieldSpec = {
    title: 'Number',
    kind: 'integer',
    defaultValue: 0, // 0 = no number selected
    enabledWhen: typeIn('Tool'),
    required: true,
    gte: 0,
  }

  post_lengthOffset?: number // ToolMilling, ToolHoleMaking, ToolCutting, ToolDepositing, probe
  static post_lengthOffset_spec: FieldSpec = {
    title: 'Length Offset',
    kind: 'integer',
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'ToolCutting',
      'ToolDepositing',
      'probe',
    ),
    required: true,
    gte: 0,
    dependencies: keys('post_number'),
    compute: (values: Tool_V1_Data) => values?.post_number,
  }

  post_diameterOffset?: number // ToolMilling, ToolHoleMaking, ToolCutting, probe
  static post_diameterOffset_spec: FieldSpec = {
    title: 'Diameter Offset',
    kind: 'integer',
    defaultValue: 0,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'ToolCutting',
      'probe',
    ),
    required: true,
    gte: 0,
    dependencies: keys('post_number'),
    compute: (values: Tool_V1_Data) => values?.post_number,
  }

  post_comment?: string // Tool
  static post_comment_spec: FieldSpec = {
    title: 'Comment',
    kind: 'text',
    defaultValue: '',
    enabledWhen: typeIn('Tool'),
  }

  post_manualToolChange?: boolean // ToolMilling, ToolHoleMaking, ToolTurning, ToolCutting, probe
  static post_manualToolChange_spec: FieldSpec = {
    title: 'Manual Tool Change',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'ToolTurning',
      'ToolCutting',
      'probe',
    ),
    required: true,
  }

  post_breakControl?: boolean // ToolMilling, ToolHoleMaking, ToolTurning, probe
  static post_breakControl_spec: FieldSpec = {
    title: 'Break Control',
    kind: 'boolean',
    defaultValue: false,
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'ToolTurning',
      'probe',
    ),
    required: true,
  }

  post_turret?: number // ToolMilling, ToolHoleMaking, ToolTurning, probe
  static post_turret_spec: FieldSpec = {
    title: 'Turret',
    kind: 'integer',
    defaultValue: 0,
    hidden: !supportTurning, // pass through, but don't expose until we add turning
    enabledWhen: typeIn(
      'ToolMilling',
      'ToolHoleMaking',
      'ToolTurning',
      'probe',
    ),
    required: true,
  }

  post_live?: boolean // ToolMilling, ToolHoleMaking, probe
  static post_live_spec: FieldSpec = {
    title: 'Live Tool',
    kind: 'boolean',
    hidden: !supportTurning, // pass through, but don't expose until we add turning
    defaultValue: true,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'probe'),
    required: true,
  }

  //////////// holder ////////////

  static groupHolder_spec: FieldSpec = { title: 'Holder', kind: 'group' }

  /*
  holder_unit?: UnitLonghand // ToolMilling, ToolHoleMaking, holder, probe
  static holder_unit_spec: FieldSpec = {
    title: 'Unit',
    kind: 'enum',
    forceNarrow: true,
    defaultValue: 'millimeters' as UnitLonghand,
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_guid?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_guid_spec: FieldSpec = {
    title: 'Holder GUID',
    kind: 'guid',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
    hidden: true,
  }

  holder_description?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_description_spec: FieldSpec = {
    title: 'Holder Description',
    kind: 'text',
    defaultValue: '',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_productId?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_productId_spec: FieldSpec = {
    title: 'Holder Product ID',
    kind: 'text',
    defaultValue: '',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_productLink?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_productLink_spec: FieldSpec = {
    title: 'Holder Product Link',
    kind: 'text',
    defaultValue: '',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_vendor?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_vendor_spec: FieldSpec = {
    title: 'Holder Vendor',
    kind: 'text',
    defaultValue: '',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_gaugeLength?: number // ToolMilling, ToolHoleMaking, holder, probe
  static holder_gaugeLength_spec: FieldSpec = {
    title: 'Holder Gauge Length',
    kind: 'length',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }

  holder_segments?: string // ToolMilling, ToolHoleMaking, holder, probe
  static holder_segments_spec: FieldSpec = {
    title: 'Holder Segments',
    kind: 'segments',
    enabledWhen: typeIn('ToolMilling', 'ToolHoleMaking', 'holder', 'probe'),
  }
*/
  static version: number = 1
}
/*
// prettier-ignore
export class ToolTurning_V1 extends ToolBase_V1 {
  //////////// cutter ////////////
  
  material?: string;             // ToolMilling, ToolHoleMaking, ToolTurning
  static material_spec: FieldSpec = {"title":"Material","kind":"enum","defaultValue":"carbide","for_types":["ToolMilling","ToolHoleMaking","ToolTurning"]};
  
  //////////// cutter_geometry ////////////
  
  geometry_cornerRadius?: number;         // bull nose end mill, face mill, tapered mill, radius mill, dovetail mill, slot mill, circle segment lens, turning general, turning boring, turning grooving
  static geometry_cornerRadius_spec: FieldSpec = {"title":"Corner Radius","kind":"length","defaultValue":0.75,"for_types":["bull nose end mill","face mill","tapered mill","radius mill","dovetail mill","slot mill","circle segment lens","turning general","turning boring","turning grooving"]};
  
  geometry_overallLength?: number;        // ToolMilling, ToolHoleMaking, turning grooving, turning threading
  static geometry_overallLength_spec: FieldSpec = {"title":"Overall Length","kind":"length","defaultValue":36,"for_types":["ToolMilling","ToolHoleMaking","turning grooving","turning threading"]};
  
  geometry_threadPitch?: number;          // ToolTap, thread mill, turning threading
  static geometry_threadPitch_spec: FieldSpec = {"title":"Thread Pitch","kind":"length","defaultValue":1,"for_types":["ToolTap","thread mill","turning threading"]};
  
  geometry_threadProfileAngle?: number;   // thread mill, turning threading
  static geometry_threadProfileAngle_spec: FieldSpec = {"title":"Thread Profile Angle","kind":"angle","defaultValue":60,"for_types":["thread mill","turning threading"]};
  
  //////////// post ////////////
  
  post_number?: number;                   // Tool
  static post_number_spec: FieldSpec = {"title":"Number","kind":"integer","defaultValue":1,"for_types":["Tool"]};
  
  post_compensationOffset?: number;       // ToolTurning
  static post_compensationOffset_spec: FieldSpec = {"title":"Compensation Offset","kind":"integer","defaultValue":1,"for_types":["ToolTurning"]};
  
  post_turret?: number;                   // ToolMilling, ToolHoleMaking, ToolTurning, probe
  static post_turret_spec: FieldSpec = {"title":"Turret","kind":"integer","defaultValue":0,"for_types":["ToolMilling","ToolHoleMaking","ToolTurning","probe"]};
  
  post_comment?: string;                  // Tool
  static post_comment_spec: FieldSpec = {"title":"Comment","kind":"text","defaultValue":"","for_types":["Tool"]};
  
  post_manualToolChange?: boolean;        // ToolMilling, ToolHoleMaking, ToolTurning, ToolCutting, probe
  static post_manualToolChange_spec: FieldSpec = {"title":"Manual Tool Change","kind":"boolean","defaultValue":false,"for_types":["ToolMilling","ToolHoleMaking","ToolTurning","ToolCutting","probe"]};
  
  post_breakControl?: boolean;            // ToolMilling, ToolHoleMaking, ToolTurning, probe
  static post_breakControl_spec: FieldSpec = {"title":"Break Control","kind":"boolean","defaultValue":false,"for_types":["ToolMilling","ToolHoleMaking","ToolTurning","probe"]};
  
  //////////// turning_insert ////////////
  
  insert_type?: string;          // ToolTurning
  static insert_type_spec: FieldSpec = {"title":"Shape","kind":"enum","for_types":["ToolTurning"]};
  
  insert_reliefAngle?: number;            // turning general, turning boring
  static insert_reliefAngle_spec: FieldSpec = {"title":"Relief Angle","kind":"angle","for_types":["turning general","turning boring"]};
  
  insert_toleranceClass?: string;// ToolTurning
  static insert_toleranceClass_spec: FieldSpec = {"title":"Tolerance","kind":"enum","defaultValue":"M","for_types":["ToolTurning"]};
  
  insert_crossSection?: string;  // ToolTurning
  static insert_crossSection_spec: FieldSpec = {"title":"Cross Section","kind":"enum","for_types":["ToolTurning"]};
  
  insert_sizeSpecificationMode?: string;// turning general, turning boring
  static insert_sizeSpecificationMode_spec: FieldSpec = {"title":"Size specified by","kind":"enum","defaultValue":"CE","for_types":["turning general","turning boring"]};
  
  insert_diameter?: number;               // turning general, turning boring
  static insert_diameter_spec: FieldSpec = {"title":"Insert size","kind":"length","for_types":["turning general","turning boring"]};
  
  insert_thickness?: number;              // ToolTurning
  static insert_thickness_spec: FieldSpec = {"title":"Thickness","kind":"length","for_types":["ToolTurning"]};
  
  insert_headLength?: number;             // turning grooving
  static insert_headLength_spec: FieldSpec = {"title":"Head Length","kind":"length","for_types":["turning grooving"]};
  
  insert_grooveWidth?: number;            // turning grooving
  static insert_grooveWidth_spec: FieldSpec = {"title":"Groove Width","kind":"length","for_types":["turning grooving"]};
  
  insert_angle?: number;                  // turning general
  static insert_angle_spec: FieldSpec = {"title":"Angle","kind":"angle","for_types":["turning general"]};
  
  insert_leadingAngle?: number;           // turning general
  static insert_leadingAngle_spec: FieldSpec = {"title":"Leading Angle","kind":"angle","for_types":["turning general"]};
  
  insert_lengthNonCuttingEdge?: number;   // turning general
  static insert_lengthNonCuttingEdge_spec: FieldSpec = {"title":"Trailing edge length","kind":"length","for_types":["turning general"]};
  
  insert_width?: number;                  // turning grooving, turning threading
  static insert_width_spec: FieldSpec = {"title":"Width","kind":"length","for_types":["turning grooving","turning threading"]};
  
  insert_minimumThreadPitch?: number;     // turning threading
  static insert_minimumThreadPitch_spec: FieldSpec = {"title":"Minimum Thread Pitch","kind":"length","for_types":["turning threading"]};
  
  insert_maximumThreadPitch?: number;     // turning threading
  static insert_maximumThreadPitch_spec: FieldSpec = {"title":"Maximum Thread Pitch","kind":"length","for_types":["turning threading"]};
  
  insert_internalThread?: boolean;        // turning threading
  static insert_internalThread_spec: FieldSpec = {"title":"Internal Thread","kind":"boolean","defaultValue":"false","for_types":["turning threading"]};
  
  insert_threadTipRadius?: number;        // turning threading
  static insert_threadTipRadius_spec: FieldSpec = {"title":"Thread Tip Radius","kind":"length","for_types":["turning threading"]};
  
  insert_threadTipType?: string; // turning threading
  static insert_threadTipType_spec: FieldSpec = {"title":"Thread Tip Type","kind":"enum","defaultValue":"round","for_types":["turning threading"]};
  
  insert_threadTipWidth?: number;         // turning threading
  static insert_threadTipWidth_spec: FieldSpec = {"title":"Thread Tip Width","kind":"length","for_types":["turning threading"]};
  
  //////////// turning_holder ////////////
  
  holder_style?: string;         // ToolTurning
  static holder_style_spec: FieldSpec = {"title":"Style","kind":"enum","defaultValue":"L","for_types":["ToolTurning"]};
  
  holder_hand?: string;          // ToolTurning
  static holder_hand_spec: FieldSpec = {"title":"Hand","kind":"enum","defaultValue":"R","for_types":["ToolTurning"]};
  
  holder_clamping?: string;      // ToolTurning
  static holder_clamping_spec: FieldSpec = {"title":"Clamping","kind":"enum","defaultValue":"D","for_types":["ToolTurning"]};
  
  holder_cuttingWidth?: number;           // turning general, turning boring
  static holder_cuttingWidth_spec: FieldSpec = {"title":"Cutting Width","kind":"length","for_types":["turning general","turning boring"]};
  
  holder_headLength?: number;             // ToolTurning
  static holder_headLength_spec: FieldSpec = {"title":"Head Length","kind":"length","for_types":["ToolTurning"]};
  
  holder_overallLength?: number;          // ToolTurning
  static holder_overallLength_spec: FieldSpec = {"title":"Overall Length","kind":"length","for_types":["ToolTurning"]};
  
  holder_shankHeight?: number;            // turning general, turning grooving, turning threading
  static holder_shankHeight_spec: FieldSpec = {"title":"Shank Height","kind":"length","for_types":["turning general","turning grooving","turning threading"]};
  
  holder_shankWidth?: number;             // ToolTurning
  static holder_shankWidth_spec: FieldSpec = {"title":"Shank Width","kind":"length","for_types":["ToolTurning"]};
  
  holder_roundShank?: boolean;            // turning general, turning grooving, turning threading
  static holder_roundShank_spec: FieldSpec = {"title":"Round Shank","kind":"boolean","defaultValue":false,"for_types":["turning general","turning grooving","turning threading"]};

  holder_sideAngle?: number
  
  holder_sideCutting?: boolean
  
  //////////// turning_setup ////////////
  
  setup_angle?: number;                   // ToolTurning
  static setup_angle_spec: FieldSpec = {"title":"Orientation","kind":"angle","defaultValue":0,"for_types":["ToolTurning"]};
  
  setup_compensation?: string;   // ToolTurning
  static setup_compensation_spec: FieldSpec = {"title":"Compensation","kind":"enum","for_types":["ToolTurning"]};
  
  setup_clockwise?: boolean;              // ToolTurning
  static setup_clockwise_spec: FieldSpec = {"title":"Clockwise Spindle Rotation","kind":"boolean","defaultValue":"R","for_types":["ToolTurning"]};
  
  setup_grooveCompOppositeEdge?: boolean; // turning grooving
  static setup_grooveCompOppositeEdge_spec: FieldSpec = {"title":"Use Opposite Edge","kind":"boolean","for_types":["turning grooving"]};
  
  static version: number = 1;
}

// prettier-ignore
export class ToolCutting_V1 extends ToolBase_V1 {
  //////////// cutter_geometry ////////////
  
  geometry_nozzleDiameter?: number;       // ToolCutting
  static geometry_nozzleDiameter_spec: FieldSpec = {"title":"Nozzle Diameter","kind":"length","defaultValue":1,"for_types":["ToolCutting"]};
  
  geometry_kerfWidth?: number;            // ToolCutting
  static geometry_kerfWidth_spec: FieldSpec = {"title":"Kerf Width","kind":"length","defaultValue":1,"for_types":["ToolCutting"]};
  
  geometry_headClearance?: number;        // ToolCutting
  static geometry_headClearance_spec: FieldSpec = {"title":"Head Clearance","kind":"length","defaultValue":1,"for_types":["ToolCutting"),"hidden":true};
  
  //////////// post ////////////
  
  post_number?: number;                   // Tool
  static post_number_spec: FieldSpec = {"title":"Number","kind":"integer","defaultValue":1,"for_types":["Tool"]};
  
  post_lengthOffset?: number;             // ToolMilling, ToolHoleMaking, ToolCutting, ToolDepositing, probe
  static post_lengthOffset_spec: FieldSpec = {"title":"Length Offset","kind":"integer","defaultValue":1,"for_types":["ToolMilling","ToolHoleMaking","ToolCutting","ToolDepositing","probe"]};
  
  post_diameterOffset?: number;           // ToolMilling, ToolHoleMaking, ToolCutting, probe
  static post_diameterOffset_spec: FieldSpec = {"title":"Diameter Offset","kind":"integer","defaultValue":1,"for_types":["ToolMilling","ToolHoleMaking","ToolCutting","probe"]};
  
  post_comment?: string;                  // Tool
  static post_comment_spec: FieldSpec = {"title":"Comment","kind":"text","defaultValue":"","for_types":["Tool"]};
  
  post_manualToolChange?: boolean;        // ToolMilling, ToolHoleMaking, ToolTurning, ToolCutting, probe
  static post_manualToolChange_spec: FieldSpec = {"title":"Manual Tool Change","kind":"boolean","defaultValue":false,"for_types":["ToolMilling","ToolHoleMaking","ToolTurning","ToolCutting","probe"]};
  
  static version: number = 1;
}
*/

// helper to get type checked keys
function keys<K extends keyof Tool_V1_Data>(...args: K[]): K[] {
  return args
}

export type Tool_V1_Data = InstanceProperties<Tool_V1>
//export type ToolTurning_V1_Data = InstanceProperties<ToolTurning_V1>
//export type ToolCutting_V1_Data = InstanceProperties<ToolCutting_V1>

//export type ToolAll_V1_Data = Tool_V1_Data & ToolTurning_V1_Data & ToolCutting_V1_Data
